/**
 * Algolia Header Search Bar — self-contained Preact search (input + popover)
 *
 * Renders its OWN input + results popover into the (server-side empty)
 * #header-search-root element. Because no native <input name="q"> exists in
 * the initial HTML, the Algolia Shopify app's native autocomplete has nothing
 * to attach to — eliminating the conflict that broke the custom search bar.
 * (Mirrors the Hartville Hardware pattern. See docs/action-items.txt.)
 *
 * Reads window.preact (UMD, loaded via snippets/preact-cdn.liquid).
 * Reads Algolia credentials natively from window.algoliaShopify.config
 * (injected by the Algolia AI Search & Discovery app).
 */
(function () {
  'use strict';

  // ─── Wait for Preact + DOM ─────────────────────────────────────────────────
  function init() {
    if (!window.preact || !window.preact.html) {
      setTimeout(init, 50);
      return;
    }
    // Mount into the desktop root and the mobile root. Only one is visible per
    // breakpoint (CSS), so the two instances never interfere. Both are rendered
    // EMPTY server-side, so the Algolia app's native autocomplete never attaches.
    ['header-search-root', 'header-search-root-mobile'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) mountAlgoliaSearch(el);
    });
  }

  // ─── Config ────────────────────────────────────────────────────────────────
  var DEBOUNCE_MS = (window.algoliaConfig && window.algoliaConfig.searchDebounceMs) || 300;
  var MIN_QUERY_LENGTH = (window.algoliaConfig && window.algoliaConfig.searchMinQueryLength) || 3;

  function getAlgoliaConfig() {
    var c = (window.algoliaShopify && window.algoliaShopify.config) || {};
    var prefix = c.index_prefix || 'shopify_';

    return {
      appId: c.app_id || '',
      searchKey: c.search_api_key || '',
      suggestionsIndex: c.query_suggestions_index_name || (prefix + 'products_query_suggestions'),
      collectionsIndex: prefix + 'collections',
      pagesIndex: prefix + 'pages',
      articlesIndex: prefix + 'articles',
      productsIndex: c.products_index_name || (prefix + 'products'),
      maxSuggestions: c.suggestions_autocomplete_hits_per_page || 5,
      maxCollections: c.collections_autocomplete_hits_per_page || 3,
      maxPages: c.pages_autocomplete_hits_per_page || 3,
      maxArticles: c.articles_autocomplete_hits_per_page || 3,
      maxProducts: c.products_autocomplete_hits_per_page || 6, // clean multiple of 3 (3-across grid)
      showSuggestions: c.index_query_suggestions !== false, // products_query_suggestions index now exists in Algolia (mirrors Hartville)
      showCollections: c.index_collections !== false,
      showPages: c.index_pages !== false,
      showArticles: c.index_articles !== false,
      showProducts: c.index_products !== false,
      stockPolicy: c.stock_policy || 'allow',
    };
  }

  // ─── Search client ─────────────────────────────────────────────────────────
  function getSearchClient() {
    if (window.algoliaShopify && window.algoliaShopify.searchClient) {
      return window.algoliaShopify.searchClient;
    }
    // Fallback: use algoliasearch-lite UMD if loaded
    if (typeof algoliasearch === 'function') {
      var cfg = getAlgoliaConfig();
      if (cfg.appId && cfg.searchKey) {
        if (!window.__hsbSearchClient) {
          window.__hsbSearchClient = algoliasearch(cfg.appId, cfg.searchKey);
        }
        return window.__hsbSearchClient;
      }
    }
    return null;
  }

  // ─── Utilities ────────────────────────────────────────────────────────────
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  function asBool(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      var n = value.trim().toLowerCase();
      return n === 'true' || n === '1' || n === 'yes';
    }
    return false;
  }

  function getExceptionType(hit) {
    if (hit && hit.template_suffix === 'rental') return 'rental';
    var raw = hit && hit.meta && hit.meta.custom && hit.meta.custom.brand_s
      && hit.meta.custom.brand_s.fields && hit.meta.custom.brand_s.fields.hide_price;
    var hidePrice = asBool(raw && raw.value !== undefined ? raw.value : raw);
    return hidePrice ? 'hide_price' : null;
  }

  /** Format a price value (string or number) → HTML with superscript cents */
  function formatPrice(val, html) {
    if (val === null || val === undefined || val === '') val = '0.00';
    var parts = String(val).split('.');
    var dollars = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var cents = ((parts[1] || '00') + '00').substring(0, 2);
    if (html) {
      return html`$${dollars}<sup class="hsb-price-cents">.${cents}</sup>`;
    }
    return '$' + dollars + '.' + cents;
  }

  /** Get the Algolia highlight HTML string for a field, or plain text fallback */
  function getHighlightHtml(hit, attr) {
    var hl = hit._highlightResult && hit._highlightResult[attr];
    if (hl && hl.value) return hl.value;
    return hit[attr] ? String(hit[attr]).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
  }

  /**
   * Pick a card's main image. Algolia distinct returns a representative VARIANT
   * record, so hit.image can be a child/accessory image. Default to the
   * product's main image (hit.product_image); only use the variant image when
   * the query matched the variant (variant_title/sku) more strongly than the
   * product title. Mirrors pickPlpCardImage in algolia-native.js / plp-app.js.
   */
  function pickCardImage(hit) {
    if (!hit) return '';
    var hr = hit._highlightResult || {};
    function matched(key) {
      var v = hr[key];
      if (!v) return 0;
      if (Array.isArray(v)) return v.reduce(function (a, x) { return a + ((x && x.matchedWords && x.matchedWords.length) || 0); }, 0);
      return (v.matchedWords && v.matchedWords.length) || 0;
    }
    var titleN = matched('title');
    var variantN = Math.max(matched('variant_title'), matched('sku'), matched('option1'), matched('option2'), matched('option3'));
    var variantImg = hit.image || hit.variant_image;
    var productImg = hit.product_image;
    var useVariant = variantN > 0 && variantN >= titleN; // tie w/ a variant match → variant image; no-query → product
    var primary = useVariant ? (variantImg || productImg) : (productImg || variantImg);
    return primary || variantImg || productImg || '';
  }

  // ─── Discounts ─────────────────────────────────────────────────────────────
  // Resolve through the shared StorefrontClient — the single gateway every
  // surface uses (PLP, search, PDP, cards, cart). getDiscounts() already serves
  // from the unified localStorage cache (1h TTL) shared across all of them,
  // dedups in-flight variants and applies the order-level guardrail, so the
  // popover stays consistent without touching any per-surface state.
  function fetchDiscountsForHits(hits) {
    var discounts = new Map();
    if (!hits || !hits.length || !window.StorefrontClient) return Promise.resolve(discounts);

    var vids = hits.map(function (hit) {
      return String(
        hit.variant_id || hit.variantId
        || (hit.variants && hit.variants[0] && hit.variants[0].id)
        || hit.objectID
      );
    });

    return window.StorefrontClient.getDiscountsIsolated(vids).then(function (map) {
      map.forEach(function (info, id) {
        // null/unresolved = undetermined — show the base Algolia price
        discounts.set(String(id), info && info.hasDiscount ? info : { hasDiscount: false });
      });
      return discounts;
    }).catch(function () { return discounts; });
  }

  // ─── Desktop search-bar expand animation ──────────────────────────────────
  // Mirrors Hartville: on focus the right-side .header-utilities collapse and
  // the flex:1 inline search fills the freed space. To make the collapse
  // animate smoothly (auto-width → 0 isn't transitionable) we pin the real
  // width as max-width, force a reflow, then drop it to 0 on the next frame.
  // overflow:hidden is toggled inline so the resting layout never clips the
  // cart-count bubble. The opacity/offset come from the .sba-search-expanded
  // CSS rule; the transition itself is declared on .header-utilities.
  function handleSearchFocus() {
    if (window.innerWidth < 990) return; /* desktop inline search shows at >=990px */
    var headerEl = document.querySelector('.header-wrapper');
    if (!headerEl || headerEl.classList.contains('sba-search-expanded')) return;
    var utils = headerEl.querySelector('.header-utilities');
    if (utils) {
      utils.style.overflow = 'hidden';
      utils.style.maxWidth = utils.scrollWidth + 'px'; /* pin current width */
      void utils.offsetWidth;                          /* force reflow */
      utils.style.maxWidth = '0px';                    /* animate to collapsed */
    }
    headerEl.classList.add('sba-search-expanded');
  }

  function removeSearchExpanded() {
    var headerEl = document.querySelector('.header-wrapper');
    if (!headerEl) return;
    headerEl.classList.remove('sba-search-expanded');
    var utils = headerEl.querySelector('.header-utilities');
    if (!utils || utils.style.maxWidth === '') return; /* wasn't expanded */
    if (window.innerWidth < 990) {
      // Resized to mobile mid-expand: just drop the inline caps, no animation.
      utils.style.maxWidth = '';
      utils.style.overflow = '';
      return;
    }
    // Animate width back to its content size, then clear so layout stays fluid.
    utils.style.maxWidth = utils.scrollWidth + 'px';
    var clear = function (e) {
      if (e && e.propertyName && e.propertyName !== 'max-width') return;
      utils.style.maxWidth = '';
      utils.style.overflow = '';
      utils.removeEventListener('transitionend', clear);
    };
    utils.addEventListener('transitionend', clear);
  }

  // ─── Multi-index Algolia search ────────────────────────────────────────────
  function multiSearch(query) {
    var empty = {
      suggestions: [], collections: [], pages: [], articles: [],
      products: [], productsQueryID: null, redirectUrl: null,
    };
    if (!query.trim()) return Promise.resolve(empty);

    var client = getSearchClient();
    if (!client) {
      console.warn('[HSB] Algolia searchClient not available');
      return Promise.resolve(empty);
    }

    var cfg = getAlgoliaConfig();
    var queries = [];
    var indexMap = [];

    if (cfg.showSuggestions) {
      queries.push({
        indexName: cfg.suggestionsIndex,
        params: { query: query, hitsPerPage: cfg.maxSuggestions, attributesToRetrieve: ['query', 'objectID'] },
      });
      indexMap.push('suggestions');
    }
    if (cfg.showCollections) {
      queries.push({ indexName: cfg.collectionsIndex, params: { query: query, hitsPerPage: cfg.maxCollections } });
      indexMap.push('collections');
    }
    if (cfg.showPages) {
      queries.push({ indexName: cfg.pagesIndex, params: { query: query, hitsPerPage: cfg.maxPages } });
      indexMap.push('pages');
    }
    if (cfg.showArticles) {
      queries.push({ indexName: cfg.articlesIndex, params: { query: query, hitsPerPage: cfg.maxArticles } });
      indexMap.push('articles');
    }
    if (cfg.showProducts) {
      var productParams = {
        query: query,
        hitsPerPage: cfg.maxProducts,
        distinct: true,
        clickAnalytics: true,
        removeWordsIfNoResults: 'allOptional',
        userToken: window.__algoliaUserToken || undefined,
      };
      if (cfg.stockPolicy !== 'allow') {
        productParams.filters = 'inventory_available > 0';
      }
      queries.push({ indexName: cfg.productsIndex, params: productParams });
      indexMap.push('products');
    }

    return client.search(queries).then(function (response) {
      var r = response.results || [];

      // Check for redirect rules in any result set
      var redirectUrl = null;
      for (var i = 0; i < r.length; i++) {
        var res = r[i];
        var rUrl = res && res.renderingContent && res.renderingContent.redirect && res.renderingContent.redirect.url;
        if (rUrl) { redirectUrl = rUrl; break; }

        var ud = Array.isArray(res && res.userData)
          ? res.userData
          : (res && res.userData ? [res.userData] : []);
        for (var j = 0; j < ud.length; j++) {
          if (ud[j] && ud[j].redirect) { redirectUrl = ud[j].redirect; break; }
        }
        if (redirectUrl) break;
      }

      var out = {
        suggestions: [], collections: [], pages: [], articles: [],
        products: [], productsQueryID: null, redirectUrl: redirectUrl,
      };
      indexMap.forEach(function (type, idx) {
        out[type] = (r[idx] && r[idx].hits) || [];
        if (type === 'products') out.productsQueryID = (r[idx] && r[idx].queryID) || null;
      });

      return out;
    });
  }

  // ─── Mount Preact search (input + popover) ─────────────────────────────────
  function mountAlgoliaSearch(rootEl) {
    var html = window.preact.html;
    var render = window.preact.render;
    var useState = window.preact.useState;
    var useEffect = window.preact.useEffect;
    var useRef = window.preact.useRef;
    var useCallback = window.preact.useCallback;

    // ── SidebarSection ───────────────────────────────────────────────────
    function SidebarSection(props) {
      var title = props.title;
      var items = props.items;
      var type = props.type;

      if (!items || !items.length) return null;

      function getUrl(item) {
        if (type === 'suggestions') return '/search?q=' + encodeURIComponent(item.query || item.objectID);
        if (type === 'collections') return item.url || ('/collections/' + (item.handle || item.objectID));
        if (type === 'pages') return item.url || ('/pages/' + (item.handle || item.objectID));
        if (type === 'articles') {
          return item.url || ('/blogs/' + ((item.blog && item.blog.handle) || 'news') + '/' + (item.handle || item.objectID));
        }
        return '#';
      }

      return html`
        <div class="hsb-sidebar-section">
          <span class="hsb-sidebar-title">${title}</span>
          <div class="hsb-sidebar-divider"></div>
          <ul class="hsb-sidebar-list">
            ${items.map(function (item) {
              var labelHtml = type === 'suggestions'
                ? getHighlightHtml(item, 'query')
                : getHighlightHtml(item, 'title');
              return html`
                <li key=${item.objectID}>
                  <a href=${getUrl(item)} class="hsb-sidebar-item"
                    dangerouslySetInnerHTML=${{ __html: labelHtml }}
                  ></a>
                </li>
              `;
            })}
          </ul>
        </div>
      `;
    }

    // ── ProductCard ──────────────────────────────────────────────────────
    function ProductCard(props) {
      var hit = props.hit;
      var queryID = props.queryID;
      var position = props.position;
      var discountMap = props.discountMap;

      var img = pickCardImage(hit);
      var title = hit.title || '';
      var baseUrl = hit.url || ('/products/' + (hit.handle || hit.objectID));
      var variantId = hit.variant_id || hit.variantId
        || (hit.variants && hit.variants[0] && hit.variants[0].id)
        || hit.objectID;
      var baseUrlWithVariant = (variantId && hit.handle)
        ? baseUrl + '?variant=' + variantId
        : baseUrl;
      var cfg = getAlgoliaConfig();
      var indexName = cfg.productsIndex;

      var url = (queryID && hit.objectID)
        ? baseUrlWithVariant + (baseUrlWithVariant.includes('?') ? '&' : '?')
          + 'alg_qid=' + queryID
          + '&alg_oid=' + hit.objectID
          + '&alg_idx=' + encodeURIComponent(indexName)
        : baseUrlWithVariant;

      var vendor = hit.vendor || '';
      var exceptionType = getExceptionType(hit);
      var isException = !!exceptionType;
      var exceptionLabel = exceptionType === 'hide_price' ? 'Call for Price!' : 'Rental Item';

      var discountInfo = discountMap && discountMap.get(String(variantId));
      var algoliaOnSale = !!(hit.compare_at_price && parseFloat(hit.compare_at_price) > parseFloat(hit.price || 0));
      var effectivePrice = hit.price || '0.00';
      var effectiveCompareAt = hit.compare_at_price;
      var isOnSale = algoliaOnSale;

      if (discountInfo && discountInfo.hasDiscount) {
        var algoliaPrice = parseFloat(hit.price || 0);
        if (discountInfo.finalPrice < algoliaPrice) {
          effectivePrice = discountInfo.finalPrice.toFixed(2);
          effectiveCompareAt = discountInfo.originalPrice.toFixed(2);
          isOnSale = true;
        } else if (!algoliaOnSale) {
          effectivePrice = discountInfo.finalPrice.toFixed(2);
          effectiveCompareAt = discountInfo.originalPrice.toFixed(2);
          isOnSale = true;
        }
      }

      function onProductClick(e) {
        e.preventDefault();

        // Send click event to Algolia Insights
        var hsbCfg = window.algoliaShopify && window.algoliaShopify.config;
        if (queryID && hit.objectID && hsbCfg && hsbCfg.app_id && hsbCfg.search_api_key && window.__algoliaUserToken) {
          try {
            fetch('https://insights.algolia.io/1/events', {
              method: 'POST',
              keepalive: true,
              headers: {
                'X-Algolia-Application-Id': hsbCfg.app_id,
                'X-Algolia-API-Key': hsbCfg.search_api_key,
              },
              body: JSON.stringify({ events: [{
                eventType: 'click',
                eventName: 'Product Clicked',
                index: indexName,
                userToken: window.__algoliaUserToken,
                queryID: queryID,
                objectIDs: [hit.objectID],
                positions: [position],
              }] }),
            });
          } catch (_) {}
        }

        // Store for deferred PDP conversion tracking
        try {
          sessionStorage.setItem('algolia_last_click', JSON.stringify({
            queryID: queryID,
            objectID: hit.objectID,
            indexName: indexName,
            timestamp: Date.now(),
          }));
        } catch (_) {}

        // Persist for custom pixel purchase tracking
        if (queryID && hit.objectID) {
          try {
            var key = 'algolia_analytics_clicked_objects';
            var clicks = JSON.parse(localStorage.getItem(key) || '[]');
            clicks.push({ index: indexName, queryID: queryID, objectIDs: [hit.objectID] });
            localStorage.setItem(key, JSON.stringify(clicks));
          } catch (_) {}
        }

        window.location.href = url;
      }

      var titleHtml = getHighlightHtml(hit, 'title');
      var priceClass = 'hsb-product-price' + (isOnSale ? ' hsb-product-price--sale' : '');
      var priceSectionClass = 'hsb-product-price-section' + (isOnSale ? ' hsb-product-price-section--sale' : '');

      return html`
        <article class="hsb-product-card">
          <a href=${url} class="hsb-product-image-wrapper" onClick=${onProductClick}>
            ${img ? html`<img src=${img} alt=${title} loading="lazy" />` : ''}
          </a>
          <div class="hsb-product-content">
            <p class="hsb-product-brand">${vendor}</p>
            <a href=${url} class="hsb-product-title-link" onClick=${onProductClick}>
              <h3 class="hsb-product-title" dangerouslySetInnerHTML=${{ __html: titleHtml }}></h3>
            </a>
            ${isException
              ? html`
                  <div class="hsb-product-price-section" style="padding-top:18px">
                    <span class="hsb-rental-label">${exceptionLabel}</span>
                  </div>
                `
              : html`
                  <div class=${priceSectionClass}>
                    <span class=${priceClass}>${formatPrice(effectivePrice, html)}</span>
                    ${isOnSale ? html`<span class="hsb-product-price--compare">${formatPrice(effectiveCompareAt, html)}</span>` : ''}
                  </div>
                `
            }
          </div>
        </article>
      `;
    }

    // ── SearchPopup ──────────────────────────────────────────────────────
    function SearchPopup(props) {
      var results = props.results;
      var query = props.query;
      var isOpen = props.isOpen;
      var discountMap = props.discountMap;
      var popupRef = props.popupRef;

      var trimmed = query.trim();
      var showContent = isOpen && trimmed.length > 0;

      if (!showContent) {
        return html`<div class="hsb-popup" ref=${popupRef}></div>`;
      }

      var suggestions = results.suggestions || [];
      var collections = results.collections || [];
      var pages = results.pages || [];
      var articles = results.articles || [];
      var products = results.products || [];
      var hasResults = suggestions.length || collections.length || pages.length || articles.length || products.length;

      if (!hasResults) {
        return html`
          <div class="hsb-popup hsb-popup-open" ref=${popupRef}>
            <div class="hsb-popup-content">
              <p class="hsb-no-results">No results found for "<strong>${trimmed}</strong>"</p>
            </div>
          </div>
        `;
      }

      var searchUrl = '/search?q=' + encodeURIComponent(trimmed);
      var hasSidebar = suggestions.length || collections.length || pages.length || articles.length;

      return html`
        <div class="hsb-popup hsb-popup-open" ref=${popupRef}>
          <div class="hsb-popup-content">
            <div class="hsb-search-panel">
              ${hasSidebar ? html`
                <div class="hsb-panel-sidebar">
                  <${SidebarSection} title="Suggestions" items=${suggestions} type="suggestions" />
                  <${SidebarSection} title="Collections" items=${collections} type="collections" />
                  <${SidebarSection} title="Pages" items=${pages} type="pages" />
                  <${SidebarSection} title="Articles" items=${articles} type="articles" />
                </div>
              ` : ''}
              ${products.length > 0 ? html`
                <div class="hsb-panel-products">
                  <span class="hsb-sidebar-title">Products</span>
                  <div class="hsb-sidebar-divider"></div>
                  <div class="hsb-products-grid">
                    ${products.map(function (hit, i) {
                      return html`
                        <${ProductCard}
                          key=${hit.objectID}
                          hit=${hit}
                          queryID=${results.productsQueryID}
                          position=${i + 1}
                          discountMap=${discountMap}
                        />
                      `;
                    })}
                  </div>
                  <a href=${searchUrl} class="hsb-see-all-btn">See All Products</a>
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }

    // ── HeaderSearchBar (root) ───────────────────────────────────────────
    // Renders its OWN input + button + popover into the empty #header-search-root.
    // No native <input name="q"> exists at page load, so the Algolia app's
    // native autocomplete never attaches → no conflict with this search bar.
    function HeaderSearchBar(props) {
      var rootEl = props.rootEl;
      var placeholder = rootEl.getAttribute('data-search-placeholder') || 'What are you looking for?';
      var initialTerms = rootEl.getAttribute('data-search-terms') || '';

      // Two instances mount (desktop + mobile root). The expand/collapse acts on
      // the shared .header-wrapper, so ONLY the desktop instance may drive it —
      // otherwise the mobile instance's outside-click handler collapses what the
      // desktop instance just expanded (expand+collapse cancel out → glitch).
      var isDesktopSearch = rootEl.id === 'header-search-root';

      var queryState = useState(initialTerms);
      var query = queryState[0];
      var setQuery = queryState[1];

      var resultsState = useState({
        suggestions: [], collections: [], pages: [], articles: [],
        products: [], productsQueryID: null, redirectUrl: null,
      });
      var results = resultsState[0];
      var setResults = resultsState[1];

      var openState = useState(false);
      var isOpen = openState[0];
      var setIsOpen = openState[1];

      var discountState = useState(new Map());
      var discountMap = discountState[0];
      var setDiscountMap = discountState[1];

      var formRef = useRef(null);
      var inputRef = useRef(null);
      var popupRef = useRef(null);

      // Monotonic request token. Each search bumps it; async callbacks (the
      // Algolia response AND the slower, throttle-prone discount fetch) only
      // commit state if they are still the latest in-flight request. Without
      // this a slow response for an earlier query overwrites the results of a
      // newer one — the popover "sticks" on a stale term while you keep typing.
      var seqRef = useRef(0);

      // Keep a mutable ref to latest results so the navigate closure always has
      // up-to-date redirect info without re-creating it on every render.
      var resultsRef = useRef(results);
      useEffect(function () { resultsRef.current = results; }, [results]);

      var doSearch = useCallback(
        debounce(function (q) {
          var mySeq = ++seqRef.current; // claim this as the latest request
          if (!q.trim() || q.trim().length < MIN_QUERY_LENGTH) {
            setResults({ suggestions: [], collections: [], pages: [], articles: [], products: [], productsQueryID: null, redirectUrl: null });
            setDiscountMap(new Map());
            return;
          }
          multiSearch(q).then(function (r) {
            if (mySeq !== seqRef.current) return; // a newer query superseded us
            if (r.redirectUrl) {
              window.location.href = r.redirectUrl;
              return;
            }
            // Paint the Algolia results immediately. The discount fetch goes
            // through the throttle-prone Storefront gateway, so blocking the
            // popover on it both delays results and widens the stale-response
            // race. Clear stale discounts now, then patch them in when ready.
            setDiscountMap(new Map());
            setResults(r);

            var nonException = (r.products || []).filter(function (h) { return !getExceptionType(h); });
            if (nonException.length === 0) return;
            fetchDiscountsForHits(nonException).then(function (dm) {
              if (mySeq !== seqRef.current) return; // results moved on; drop these
              setDiscountMap(dm);
            });
          }).catch(function (err) {
            console.error('[HSB] Search error:', err);
          });
        }, DEBOUNCE_MS),
        []
      );

      function navigate() {
        var q = query.trim();
        if (!q) { window.location.href = '/search'; return; }
        // Honor a redirect rule already resolved by the last search
        if (resultsRef.current && resultsRef.current.redirectUrl) {
          window.location.href = resultsRef.current.redirectUrl;
          return;
        }
        // Fire a fresh search to catch any redirect rules before navigating
        multiSearch(q).then(function (r) {
          if (r.redirectUrl) { window.location.href = r.redirectUrl; return; }
          window.location.href = '/search?q=' + encodeURIComponent(q);
        }).catch(function () {
          window.location.href = '/search?q=' + encodeURIComponent(q);
        });
      }

      function onInput(e) {
        var val = e.target.value;
        setQuery(val);
        setIsOpen(true);
        doSearch(val);
      }

      function onFocus() {
        if (isDesktopSearch) handleSearchFocus();
        var q = query.trim();
        if (q.length < MIN_QUERY_LENGTH) return;
        setIsOpen(true);
        // Populate results if we have none yet for this term (e.g. the input is
        // pre-filled with search.terms on the /search results page).
        var hasAny = (results.products && results.products.length)
          || (results.collections && results.collections.length)
          || (results.pages && results.pages.length)
          || (results.articles && results.articles.length);
        if (!hasAny) doSearch(q);
      }

      function onKeyDown(e) {
        if (e.key === 'Escape') {
          setIsOpen(false);
          if (isDesktopSearch) removeSearchExpanded();
          if (inputRef.current) inputRef.current.blur();
        }
      }

      // Enter + button click both fire the form submit
      function onSubmit(e) {
        e.preventDefault();
        navigate();
      }

      // Close on outside click — check both the form and the popup
      useEffect(function () {
        function handler(e) {
          var insideForm = formRef.current && formRef.current.contains(e.target);
          var insidePopup = popupRef.current && popupRef.current.contains(e.target);
          if (!insideForm && !insidePopup) {
            setIsOpen(false);
            if (isDesktopSearch) removeSearchExpanded();
          }
        }
        document.addEventListener('click', handler, true);
        return function () { document.removeEventListener('click', handler, true); };
      }, []);

      return html`
        <form class="header-search-inline__form" role="search" ref=${formRef} onSubmit=${onSubmit}>
          <input
            ref=${inputRef}
            class="header-search-inline__input"
            type="search"
            placeholder=${placeholder}
            value=${query}
            onInput=${onInput}
            onFocus=${onFocus}
            onKeyDown=${onKeyDown}
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          />
          <button class="header-search-inline__button" type="submit" aria-label="Search">
            <svg fill="none" viewBox="0 0 18 19"><path fill="currentColor" fill-rule="evenodd" d="M11.03 11.68A5.784 5.784 0 1 1 2.85 3.5a5.784 5.784 0 0 1 8.18 8.18m.26 1.12a6.78 6.78 0 1 1 .72-.7l5.4 5.4a.5.5 0 1 1-.71.7z" clip-rule="evenodd"/></svg>
          </button>
          <${SearchPopup}
            results=${results}
            query=${query}
            isOpen=${isOpen}
            discountMap=${discountMap}
            popupRef=${popupRef}
          />
        </form>
      `;
    }

    // Mount the Preact search into the (empty) #header-search-root
    render(html`<${HeaderSearchBar} rootEl=${rootEl} />`, rootEl);
  }

  // Kick off AFTER every declaration above has executed. init() can run
  // synchronously (DOM ready + Preact already loaded), and mounting reads
  // DEBOUNCE_MS at that instant — starting before the assignments run would
  // capture the hoisted-but-unassigned `undefined` and memoize a 0ms debounce.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
